import React from 'react';
import { Link } from 'react-router-dom';
import InnerPageLayout from '../components/ui/InnerPageLayout';
import { Callout, ContentSection, InfoCard, InfoList } from '../components/ui/ContentBlocks';

function AccountDeletion() {
  const deletionSteps = [
    {
      platform: 'iPhone / iPad',
      icon: '📱',
      steps: [
        'Open the Strapin app and sign in',
        'Go to the Profile tab',
        'Tap Settings (gear icon)',
        'Scroll to the bottom and tap Delete Account',
        'Review what will happen, then confirm deletion',
      ],
    },
    {
      platform: 'Android',
      icon: '🤖',
      steps: [
        'Open the Strapin app and sign in',
        'Go to the Profile tab',
        'Tap Settings (gear icon)',
        'Scroll to the bottom and tap Delete Account',
        'Review what will happen, then confirm deletion',
      ],
    },
    {
      platform: 'Web',
      icon: '🌐',
      steps: [
        'Sign in at the Strapin web app',
        'Open Settings from the main navigation',
        'Scroll to the bottom and tap Delete Account',
        'Confirm when prompted',
      ],
    },
  ];

  const deletionEffects = [
    'Your profile will be immediately hidden from other users',
    'All your sessions, messages, and albums will be deleted',
    'Your chats, messages, and albums will be removed',
    'Your account data will be permanently deleted after 15 days',
  ];

  return (
    <InnerPageLayout
      tag="Account Management"
      title="Delete Your"
      titleAccent="Account"
      description="You can delete your Strapin account at any time. This page explains how to do it in the app or on the web, and what happens to your data."
      compactHero
    >
      <ContentSection title="How to Delete Your Account">
        <p className="mb-5 text-silver leading-relaxed">
          Account deletion must be done while signed in. Choose your platform below and follow the steps.
        </p>
        <div className="grid gap-4">
          {deletionSteps.map((item) => (
            <InfoCard key={item.platform} icon={item.icon} title={item.platform}>
              <ol className="list-decimal space-y-2 pl-5 text-mist leading-relaxed">
                {item.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </InfoCard>
          ))}
        </div>
      </ContentSection>

      <ContentSection title="What Happens When You Delete">
        <p className="mb-4 text-silver leading-relaxed">
          Before you confirm, Strapin shows a summary of what deletion means. In general:
        </p>
        <InfoList items={deletionEffects} />
        <Callout>
          Deletion is <strong className="text-white">permanent</strong>. Once confirmed, your account cannot be recovered.
          If you only want a break, use <strong className="text-white">Logout</strong> instead — your data stays available when you sign back in.
        </Callout>
      </ContentSection>

      <ContentSection title="Strapin Pro Subscriptions">
        <p className="mb-4 text-silver leading-relaxed">
          Deleting your Strapin account does not automatically cancel an active Strapin Pro subscription. Cancel it separately:
        </p>
        <InfoList
          bullet="→"
          items={[
            'iOS: Settings app → Apple ID → Subscriptions → Strapin → Cancel',
            'Android: Google Play → Payments & subscriptions → Subscriptions → Strapin → Cancel',
          ]}
        />
      </ContentSection>

      <ContentSection title="Can't Access the App?">
        <p className="mb-4 text-silver leading-relaxed">
          If you cannot sign in or use the in-app delete option, email us from the address linked to your Strapin account.
          We will verify your identity and process the deletion request within 30 days.
        </p>
        <div className="glass-card p-5">
          <p className="mb-2 text-mist">
            📧{' '}
            <a href="mailto:support@strapinapp.com?subject=Account%20Deletion%20Request" className="font-bold text-primary hover:underline">
              support@strapinapp.com
            </a>
          </p>
          <p className="text-sm text-silver">
            Include the email address on your account and write &quot;Account Deletion Request&quot; in the subject line.
          </p>
        </div>
      </ContentSection>

      <ContentSection title="Related Policies">
        <div className="flex flex-wrap gap-3">
          <Link to="/privacy" className="btn-ghost">Privacy Policy</Link>
          <Link to="/terms" className="btn-ghost">Terms of Service</Link>
          <Link to="/support" className="btn-primary">Contact Support</Link>
        </div>
      </ContentSection>

      <p className="text-sm text-silver">Last updated: May 2026. Strapin — Account Deletion.</p>
    </InnerPageLayout>
  );
}

export default AccountDeletion;
